// src/pages/Public/Volunteer/PublicVolunteerRegistrationForm.js

import React, { useState } from 'react';
import {
  Box, Typography, Paper, TextField, Button, FormControl, InputLabel,
  Select, MenuItem, Grid, Alert, CircularProgress, Container
} from '@mui/material';
import { CheckCircle } from '@mui/icons-material';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import backendURL from '../../../config';

const PublicVolunteerRegistrationForm = () => {
  const { chatbotId } = useParams();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  
  const [formData, setFormData] = useState({
    full_name: '',
    phone_number: '',
    email: '',
    age: '',
    city: '',
    profession: '',
    preferences: {
      availability_type: '',
      preferred_time_slot: '',
      location: '',
      event_type: '',
      notes: ''
    }
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.full_name || !formData.phone_number || !formData.email) {
      setError('Please fill in all required fields');
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError('Please enter a valid email address');
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      await axios.post(
        `${backendURL}/api/public/volunteer/register?chatbot_id=${chatbotId}`,
        formData
      );
      
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.message || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', p: 3 }}>
        <Paper sx={{ p: 4, maxWidth: 600, textAlign: 'center' }}>
          <CheckCircle sx={{ fontSize: 60, color: 'success.main', mb: 2 }} />
          <Typography variant="h4" color="success.main" gutterBottom>
            Registration Successful!
          </Typography>
          <Typography variant="body1">
            Thank you for registering as a volunteer. Your application will be reviewed and we'll contact you soon.
          </Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'grey.50', py: 4 }}>
      <Container maxWidth="md">
        <Paper sx={{ p: 4 }}>
          <Typography variant="h4" gutterBottom fontWeight={700}>
            Volunteer Registration
          </Typography>
          <Typography variant="body1" color="text.secondary" paragraph>
            Join our community of volunteers and make a difference!
          </Typography>
          
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          
          <form onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              {/* Basic Information */}
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  Basic Information
                </Typography>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  required
                  label="Full Name"
                  value={formData.full_name}
                  onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  required
                  label="Phone Number"
                  value={formData.phone_number}
                  onChange={(e) => setFormData({ ...formData, phone_number: e.target.value })}
                />
              </Grid>
              
              <Grid item xs={12} sm={8}>
                <TextField
                  fullWidth
                  required
                  type="email"
                  label="Email Address"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                />
              </Grid>
              
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  type="number"
                  label="Age"
                  value={formData.age}
                  onChange={(e) => setFormData({ ...formData, age: e.target.value })}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="City"
                  value={formData.city}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                  placeholder="Your city"
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Profession"
                  value={formData.profession}
                  onChange={(e) => setFormData({ ...formData, profession: e.target.value })}
                  placeholder="e.g., Teacher, Engineer, Student"
                />
              </Grid>

              {/* Preferences */}
              <Grid item xs={12} sx={{ mt: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Your Preferences
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>When are you available?</InputLabel>
                  <Select
                    value={formData.preferences.availability_type}
                    onChange={(e) => setFormData({
                      ...formData,
                      preferences: { ...formData.preferences, availability_type: e.target.value }
                    })}
                    label="When are you available?"
                  >
                    <MenuItem value="">Select</MenuItem>
                    <MenuItem value="WEEKEND">Weekends Only</MenuItem>
                    <MenuItem value="WEEKDAY">Weekdays Only</MenuItem>
                    <MenuItem value="BOTH">Both Weekends & Weekdays</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Preferred Time</InputLabel>
                  <Select
                    value={formData.preferences.preferred_time_slot}
                    onChange={(e) => setFormData({
                      ...formData,
                      preferences: { ...formData.preferences, preferred_time_slot: e.target.value }
                    })}
                    label="Preferred Time"
                  >
                    <MenuItem value="">Select</MenuItem>
                    <MenuItem value="MORNING_6_9">Morning (6-9 AM)</MenuItem>
                    <MenuItem value="AFTERNOON_1_3">Afternoon (1-3 PM)</MenuItem>
                    <MenuItem value="EVENING_6_8">Evening (6-8 PM)</MenuItem>
                    <MenuItem value="EVENING_6_9">Late Evening (6-9 PM)</MenuItem>
                    <MenuItem value="FLEXIBLE">Flexible</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Preferred Location"
                  value={formData.preferences.location}
                  onChange={(e) => setFormData({
                    ...formData,
                    preferences: { ...formData.preferences, location: e.target.value }
                  })}
                  placeholder="City or area"
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Event Type Interest</InputLabel>
                  <Select
                    value={formData.preferences.event_type}
                    onChange={(e) => setFormData({
                      ...formData,
                      preferences: { ...formData.preferences, event_type: e.target.value }
                    })}
                    label="Event Type Interest"
                  >
                    <MenuItem value="">Select</MenuItem>
                    <MenuItem value="RALLY">Rally Events</MenuItem>
                    <MenuItem value="DONATION">Donation Drives</MenuItem>
                    <MenuItem value="FUNDRAISING">Fundraising</MenuItem>
                    <MenuItem value="COMMUNITY_SERVICE">Community Service</MenuItem>
                    <MenuItem value="OTHER">Other</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  label="Tell us about yourself"
                  value={formData.preferences.notes}
                  onChange={(e) => setFormData({
                    ...formData,
                    preferences: { ...formData.preferences, notes: e.target.value }
                  })}
                  placeholder="Share your interests, skills, or why you want to volunteer..."
                />
              </Grid>

              <Grid item xs={12}>
                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  fullWidth
                  disabled={loading}
                >
                  {loading ? <CircularProgress size={24} /> : 'Register as Volunteer'}
                </Button>
              </Grid>
            </Grid>
          </form>
        </Paper>
      </Container>
    </Box>
  );
};

export default PublicVolunteerRegistrationForm;